import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Telescope, Info } from "lucide-react";
import Navbar from "../components/Navbar";
import { api } from "../lib/api";
import { fmtMoney, fmtPct, marginColor, displayTicker } from "../lib/format";

const HORIZONS = ["2030", "2040", "2050"];
const MARKETS_F = ["ALL", "IN", "US"];

export default function Horizons() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [horizon, setHorizon] = useState("2030");
  const [market, setMarket] = useState("ALL");
  const [undervaluedOnly, setUndervaluedOnly] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/horizons?market=ALL", { timeout: 120000 });
        setRows(data.results || []);
      } catch {
        setRows([]);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filtered = useMemo(() => {
    let arr = rows;
    if (market !== "ALL") arr = arr.filter((r) => r.market === market);
    if (undervaluedOnly) arr = arr.filter((r) => r.undervalued);
    const key = `upside_${horizon}_pct`;
    return [...arr].sort((a, b) => (b[key] ?? -1e9) - (a[key] ?? -1e9)).slice(0, 50);
  }, [rows, market, undervaluedOnly, horizon]);

  return (
    <div className="min-h-screen bg-bone-50" data-testid="horizons-page">
      <Navbar />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-14">
        <div className="mb-8">
          <div className="overline mb-3 flex items-center gap-1.5">
            <Telescope className="w-3.5 h-3.5" />— Long-Horizon Growth
          </div>
          <h1 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl text-stone_brand-dark tracking-tight font-semibold">
            2030 · 2040 · 2050 growth stocks
          </h1>
          <p className="text-stone_brand mt-2 max-w-2xl">
            Where today's growers could land decades out — your universe (India) + US mega caps,
            compounded at each stock's growth rate with decade-by-decade tapering.
          </p>
        </div>

        <div className="bg-amber_brand-bg border border-amber_brand/30 rounded-sm p-3.5 mb-6 flex items-start gap-2.5" data-testid="horizons-disclaimer">
          <Info className="w-4 h-4 text-amber_brand mt-0.5 flex-shrink-0" />
          <p className="text-xs text-stone_brand">
            <span className="font-semibold text-amber_brand">Model projections, not analyst forecasts.</span>{" "}
            No firm publishes 2040/2050 targets — these compound the latest earnings/revenue growth through a smooth damping curve (asymptote ~32% CAGR near-term, so hyper-growth names stay differentiated), decaying ~35% per decade with a 5-6% floor. Research starting points only.
          </p>
        </div>

        <div className="bg-white border border-line rounded-sm p-4 mb-6 flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-1.5" data-testid="horizon-tabs">
            {HORIZONS.map((h) => (
              <button
                key={h}
                onClick={() => setHorizon(h)}
                className={`px-4 py-1.5 rounded-sm text-xs font-mono tracking-wider transition-colors ${
                  horizon === h ? "bg-moss text-bone-50" : "bg-white border border-line text-stone_brand hover:border-moss/50"
                }`}
                data-testid={`horizon-${h}`}
              >
                {h}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-1.5" data-testid="horizon-market-filter">
            {MARKETS_F.map((m) => (
              <button
                key={m}
                onClick={() => setMarket(m)}
                className={`px-3 py-1.5 rounded-sm text-xs font-mono tracking-wider transition-colors ${
                  market === m ? "bg-stone_brand-dark text-bone-50" : "bg-white border border-line text-stone_brand hover:border-moss/50"
                }`}
                data-testid={`horizon-market-${m}`}
              >
                {m === "ALL" ? "🌍 All" : m === "IN" ? "🇮🇳 India" : "🇺🇸 US"}
              </button>
            ))}
          </div>
          <label className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-stone_brand cursor-pointer">
            <input
              type="checkbox"
              checked={undervaluedOnly}
              onChange={(e) => setUndervaluedOnly(e.target.checked)}
              className="accent-[#3f5c40]"
              data-testid="horizon-undervalued-toggle"
            />
            Undervalued only
          </label>
        </div>

        {loading ? (
          <div className="grid gap-2">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="h-14 bg-white border border-line rounded-sm animate-pulse" />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="bg-white border border-line rounded-sm p-12 text-center">
            <div className="font-serif-display text-xl text-stone_brand-dark mb-1">No projectable stocks</div>
            <p className="text-sm text-stone_brand">Growth data is missing or negative for this filter.</p>
          </div>
        ) : (
          <div className="bg-white border border-line rounded-sm overflow-x-auto" data-testid="horizons-table">
            <table className="w-full">
              <thead>
                <tr className="border-b border-line text-left">
                  <th className="overline py-4 px-3 text-stone_brand-light">#</th>
                  <th className="overline py-4 px-3 text-stone_brand-light">Ticker</th>
                  <th className="overline py-4 px-3 text-stone_brand-light">Live</th>
                  <th className="overline py-4 px-3 text-stone_brand-light">Growth CAGR</th>
                  <th className="overline py-4 px-3 text-stone_brand-light">FV Upside</th>
                  <th className="overline py-4 px-3 text-stone_brand-light bg-moss-bg/30">Target {horizon}</th>
                  <th className="overline py-4 px-3 text-stone_brand-light bg-moss-bg/30">Upside {horizon}</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r, i) => (
                  <tr
                    key={r.ticker}
                    className="border-b border-line last:border-0 hover:bg-bone-100 transition-colors cursor-pointer"
                    onClick={() => navigate(`/stock/${encodeURIComponent(r.ticker)}`)}
                    data-testid={`horizon-row-${r.ticker}`}
                  >
                    <td className="py-3 px-3 font-mono text-xs text-stone_brand-light">{String(i + 1).padStart(2, "0")}</td>
                    <td className="py-3 px-3">
                      <div className="flex items-center gap-2">
                        <span className="font-mono font-semibold text-stone_brand-dark">{displayTicker(r.ticker)}</span>
                        <span className="text-[0.6rem] font-mono uppercase tracking-widest text-stone_brand-light border border-line px-1 rounded-sm">
                          {r.market}
                        </span>
                        {r.undervalued && (
                          <span className="text-[0.55rem] font-mono uppercase tracking-widest text-moss bg-moss-bg/60 border border-moss/30 px-1.5 py-0.5 rounded-sm">
                            undervalued
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-stone_brand truncate max-w-[220px]">{r.company_name}</div>
                    </td>
                    <td className="py-3 px-3 font-mono text-sm text-stone_brand-dark">{fmtMoney(r.current_price, r.currency)}</td>
                    <td className="py-3 px-3 font-mono text-sm text-stone_brand-dark">
                      {fmtPct(r[`cagr_${horizon}_pct`])}
                      <div className="text-[0.6rem] font-mono text-stone_brand-light">now: {fmtPct(r.growth_pct)}</div>
                    </td>
                    <td className={`py-3 px-3 font-mono text-sm ${marginColor(r.fv_upside_pct)}`}>{fmtPct(r.fv_upside_pct)}</td>
                    <td className="py-3 px-3 font-mono text-sm font-semibold text-stone_brand-dark bg-moss-bg/20">
                      {fmtMoney(r[`target_${horizon}`], r.currency)}
                    </td>
                    <td className="py-3 px-3 font-mono text-base font-semibold text-moss bg-moss-bg/20">
                      {fmtPct(r[`upside_${horizon}_pct`])}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  );
}
